import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { RegionProvider } from './context/RegionContext';
import Landing from './pages/Landing';
import DevPreview from './pages/DevPreview';
import { SEO_PAGES } from './pages/Seo/seoPages';

/* Yandex.Metrika SPA page-hit tracker.
   The counter is initialised in public/index.html and registers the first
   page view. Subsequent route changes in React Router don't trigger a full
   load, so we fire `ym('hit', url)` on every location change. */
function YandexMetrikaRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.ym !== 'function') return;
    const url = location.pathname + location.search + location.hash;
    window.ym(108730236, 'hit', url, { referer: document.referrer });
  }, [location]);
  return null;
}

/* Code-split every heavy route so the initial landing JS doesn't carry
   Constructor/ChatBot/Admin etc. These routes hydrate only when navigated to. */
const Login          = lazy(() => import('./pages/Auth/Login'));
const Register       = lazy(() => import('./pages/Auth/Register'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword  = lazy(() => import('./pages/Auth/ResetPassword'));
const OAuthSuccess   = lazy(() => import('./pages/Auth/OAuthSuccess'));
const PayReturn      = lazy(() => import('./pages/Pay/PayReturn'));
const Constructor = lazy(() => import('./pages/Constructor'));
const ChatBot     = lazy(() => import('./pages/ChatBot/ChatBot'));
const Dashboard   = lazy(() => import('./pages/Dashboard'));
const Guests      = lazy(() => import('./pages/Guests'));
const Profile     = lazy(() => import('./pages/Profile'));
const WeddingSite = lazy(() => import('./pages/WeddingSite'));
const Admin       = lazy(() => import('./pages/Admin'));
const PrivacyPolicy = lazy(() => import('./pages/Legal/PrivacyPolicy'));
const Offer         = lazy(() => import('./pages/Legal/Offer'));
const SeoLanding    = lazy(() => import('./pages/Seo/SeoLanding'));
const BlogList      = lazy(() => import('./pages/Blog/BlogList'));
const BlogPost      = lazy(() => import('./pages/Blog/BlogPost'));

const Fallback = () => <div className="page-loading"><div className="spinner" /></div>;

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Fallback />;
  return user ? children : <Navigate to="/login" />;
}

/* Поддомен сайта-приглашения: <slug>.amorika.love (а локально для теста
   <slug>.localhost). Возвращает slug, если хост — поддомен сайта, иначе ''.
   Зарезервированные поддомены (www/api/...) и апекс игнорируются. */
const SITE_APEX = 'amorika.love';
const RESERVED_SUBS = new Set(['www', 'api', 'admin', 'mail', 'pro', 'm', 'static', 'cdn']);
function getSiteSlugFromHost() {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  let prefix = '';
  if (host.endsWith('.' + SITE_APEX)) prefix = host.slice(0, -(SITE_APEX.length + 1));
  else if (host.endsWith('.localhost')) prefix = host.slice(0, -'.localhost'.length); // dev
  if (!prefix || prefix.includes('.')) return '';            // апекс или многоуровневый
  if (RESERVED_SUBS.has(prefix.toLowerCase())) return '';
  return prefix;
}

export default function App() {
  const siteSlug = getSiteSlugFromHost();
  return (
    <AuthProvider>
      <RegionProvider>
      <BrowserRouter>
        <YandexMetrikaRouteTracker />
        <Suspense fallback={<Fallback />}>
          {siteSlug ? (
            /* На поддомене сайта рендерим только сам сайт-приглашение —
               любой путь ведёт на него (slug берётся из поддомена). */
            <Routes>
              <Route path="*" element={<WeddingSite slug={siteSlug} />} />
            </Routes>
          ) : (
          <Routes>
            <Route path="/" element={<Landing />} />
            {/* Региональные входы: тот же лендинг, регион ставит RegionProvider. */}
            <Route path="/by" element={<Landing />} />
            <Route path="/kz" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/oplata" element={<PayReturn />} />
            <Route path="/auth/success" element={<OAuthSuccess />} />
            <Route path="/constructor" element={<PrivateRoute><Constructor /></PrivateRoute>} />
            <Route path="/constructor/:projectId" element={<PrivateRoute><Constructor /></PrivateRoute>} />
            <Route path="/chatbot" element={<PrivateRoute><ChatBot /></PrivateRoute>} />
            <Route path="/chatbot/:projectId" element={<PrivateRoute><ChatBot /></PrivateRoute>} />
            <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            {/* Именные приглашения: список гостей и персональные ссылки. */}
            <Route path="/guests" element={<PrivateRoute><Guests /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/site/:slug" element={<WeddingSite />} />
            {/* Именное приглашение по legacy-пути; на поддомене код гостя
                читается из первого сегмента пути в самом WeddingSite. */}
            <Route path="/site/:slug/:guest" element={<WeddingSite />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/offer" element={<Offer />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            {SEO_PAGES.map(p => (
              <Route key={p.slug} path={`/${p.slug}`} element={<SeoLanding />} />
            ))}
            <Route path="/__preview/:key" element={<DevPreview />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          )}
        </Suspense>
      </BrowserRouter>
      </RegionProvider>
    </AuthProvider>
  );
}
